import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import RegistrationBars from '../components/RegistrationBars';
import './Dashboard.css';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const compact = new Intl.NumberFormat('en-IN');

const PERIODS = [7, 14, 30, 90];
const DEFAULT_PERIOD = 14;
const CUSTOMER_COLOR = '#4FA8BC';
const VENDOR_COLOR = '#F0A33F';

/* Percentage pill: up is good everywhere we use it today. */
const Delta = ({ value, suffix = 'vs prev 30d' }) => {
  if (value === null || value === undefined) {
    return <span className="kpi-delta kpi-delta-flat">No prior data</span>;
  }
  if (value === 0) {
    return <span className="kpi-delta kpi-delta-flat">No change {suffix}</span>;
  }
  const up = value > 0;
  return (
    <span className={`kpi-delta ${up ? 'kpi-delta-up' : 'kpi-delta-down'}`}>
      {up ? '▲' : '▼'} {Math.abs(value)}% {suffix}
    </span>
  );
};

const KpiCard = ({ label, value, sub, delta, to, canOpen, tone = 'brand' }) => {
  const body = (
    <>
      <span className="kpi-label">{label}</span>
      <strong className="kpi-value">{value}</strong>
      {sub && <span className="kpi-sub">{sub}</span>}
      {delta}
    </>
  );
  return canOpen && to ? (
    <Link to={to} className={`kpi kpi-${tone} kpi-link`}>{body}</Link>
  ) : (
    <div className={`kpi kpi-${tone}`}>{body}</div>
  );
};

const LISTING_ROWS = [
  { key: 'hotels', label: 'Hotels', to: '/sy-admin/hotels', permission: 'hotels.view' },
  { key: 'spas', label: 'Spas', to: '/sy-admin/spas', permission: 'spas.view' },
  { key: 'tours', label: 'Tours & Activities', to: '/sy-admin/tours', permission: 'tours.view' },
  { key: 'travelPackages', label: 'Travel Packages', to: '/sy-admin/travel-packages', permission: 'travel-packages.view' },
];

const QUICK_ACTIONS = [
  { label: 'Add Hotel', desc: 'Create a new hotel listing', to: '/sy-admin/hotels/new', permission: 'hotels.create', icon: '🏢' },
  { label: 'Add Spa', desc: 'Create a new spa listing', to: '/sy-admin/spas/new', permission: 'spas.create', icon: '💆' },
  { label: 'Add Tour', desc: 'Create a tour or activity', to: '/sy-admin/tours/new', permission: 'tours.create', icon: '🎫' },
  { label: 'Send Notification', desc: 'Push a message to the app', to: '/sy-admin/push-notifications/new', permission: 'push-notifications.create', icon: '🔔' },
  { label: 'Add Destination', desc: 'Feature a new city', to: '/sy-admin/suggested-destinations/new', permission: 'suggested-destinations.create', icon: '📍' },
  { label: 'Add Employee', desc: 'Give a teammate access', to: '/sy-admin/employees/new', permission: 'employees.create', icon: '🧑‍💼' },
];

const Dashboard = () => {
  // Daily registrations: the page owns the period and the data; both cards
  // are drawn from one response.
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [registrations, setRegistrations] = useState({ loading: true, error: '', data: null });
  // The admin panel has one light theme; the cards take a flag so a dark
  // theme can switch their tick, grid and tooltip colours later.
  const isDarkMode = false;

  const fetchRegistrations = useCallback(async (days) => {
    setRegistrations((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/dashboard/registrations`, { params: { days } });
      const body = response.data || {};
      if (body.status !== 1) {
        setRegistrations({ loading: false, error: body.msg || 'Could not load registrations', data: null });
        return;
      }
      setRegistrations({ loading: false, error: '', data: body.data });
    } catch (err) {
      setRegistrations({
        loading: false,
        error: err?.response?.data?.msg || err?.response?.data?.message || 'Could not load registrations',
        data: null,
      });
    }
  }, []);

  useEffect(() => {
    fetchRegistrations(period);
  }, [period, fetchRegistrations]);

  const { hasPermission, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE_URL}/admin/dashboard/stats`);
      if (res.data?.success) {
        setStats(res.data.data);
      } else {
        setError(res.data?.message || 'Failed to load dashboard');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  if (loading) {
    return (
      <Layout>
        <div className="container dashboard">
          <div className="page-head">
            <div>
              <div className="skeleton" style={{ width: 240, height: 30, marginBottom: 10 }} />
              <div className="skeleton" style={{ width: 320, height: 16 }} />
            </div>
          </div>
          <div className="kpi-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton" style={{ height: 124, borderRadius: 16 }} />
            ))}
          </div>
          <div className="dashboard-split">
            <div className="skeleton" style={{ height: 230, borderRadius: 16 }} />
            <div className="skeleton" style={{ height: 230, borderRadius: 16 }} />
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container dashboard">
          <div className="page-head">
            <div>
              <h1>Dashboard</h1>
            </div>
          </div>
          <div className="error-message">{error}</div>
          <button type="button" className="btn btn-secondary" onClick={fetchStats} style={{ marginTop: 14 }}>
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  const { listings, customers, revenue, subscriptions, attention, content } = stats;

  // One "waiting for approval" item per listing type, so each link opens the
  // list that actually holds those pending records.
  const PENDING_NOUNS = { hotels: 'hotel', spas: 'spa', tours: 'tour', travelPackages: 'travel package' };
  const pendingItems = LISTING_ROWS.map((row) => ({
    key: `pending-${row.key}`,
    count: listings[row.key]?.pending || 0,
    label: `${PENDING_NOUNS[row.key]}(s) waiting for approval`,
    to: `${row.to}?status=pending`,
    can: hasPermission(row.permission),
    tone: 'warn',
  }));

  const attentionItems = [
    ...pendingItems,
    {
      key: 'reports',
      count: attention.openReports,
      label: 'open report(s) from customers',
      to: '/sy-admin/reports?status=open',
      can: hasPermission('reports.view'),
      tone: 'danger',
    },
    {
      key: 'changes',
      count: attention.pendingChanges || 0,
      label: 'edit(s) to live listings waiting for approval',
      to: '/sy-admin/listing-changes?status=pending',
      can: hasPermission('listing-changes.view'),
      tone: 'warn',
    },
    {
      key: 'leads',
      count: attention.pendingOwnerLeads,
      label: 'owner(s) verified but never finished listing',
      to: '/sy-admin/owner-registrations?status=pending',
      can: hasPermission('owner-registrations.view'),
      tone: 'info',
    },
    {
      key: 'expiring',
      count: subscriptions.expiringIn7d,
      label: 'subscription(s) expiring within 7 days',
      to: '/sy-admin/subscription-history?expiring=7',
      can: hasPermission('subscription-history.view'),
      tone: 'info',
    },
  // "Needs attention" is a call to action, so only surface what this user
  // can actually open and act on.
  ].filter((item) => item.count > 0 && item.can);

  const quickActions = QUICK_ACTIONS.filter((a) => hasPermission(a.permission));
  const listingRows = LISTING_ROWS.filter((r) => hasPermission(r.permission));

  return (
    <Layout>
      <div className="container dashboard">
        <div className="page-head">
          <div>
            <h1>{greeting}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
            <p>
              Here is how {listings.totals.total > 0 ? 'your listings and customers are' : 'the platform is'} doing right now.
            </p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={fetchStats}>
            ↻ Refresh
          </button>
        </div>

        {/* ------------------------- needs attention ------------------------- */}
        {attentionItems.length > 0 && (
          <div className="attention">
            {attentionItems.map((item) => (
              <Link key={item.key} to={item.to} className={`attention-item attention-${item.tone}`}>
                <strong>{compact.format(item.count)}</strong>
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        )}

        {/* ------------------------------ KPIs ------------------------------- */}
        <div className="kpi-grid">
          <KpiCard
            label="Live listings"
            value={compact.format(listings.totals.active)}
            sub={`${compact.format(listings.totals.total)} total · ${compact.format(listings.totals.pending)} pending`}
            to="/sy-admin/hotels"
            canOpen={hasPermission('hotels.view')}
            tone="brand"
          />
          <KpiCard
            label="Customers"
            value={compact.format(customers.total)}
            sub={`${compact.format(customers.new30d)} joined in the last 30 days`}
            delta={<Delta value={customers.growthPct} />}
            to="/sy-admin/customers"
            canOpen={hasPermission('customers.view')}
            tone="violet"
          />
          <KpiCard
            label="Subscription revenue (30d)"
            value={inr.format(revenue.last30d)}
            sub={`${inr.format(revenue.total)} all time`}
            delta={<Delta value={revenue.growthPct} />}
            to="/sy-admin/subscription-history"
            canOpen={hasPermission('subscription-history.view')}
            tone="green"
          />
          <KpiCard
            label="Active subscriptions"
            value={compact.format(subscriptions.active)}
            sub={`${compact.format(subscriptions.verification)} verified · ${compact.format(subscriptions.featured)} featured`}
            to="/sy-admin/subscription-history?status=active"
            canOpen={hasPermission('subscription-history.view')}
            tone="amber"
          />
        </div>

        {/* --------------------- chart + listing breakdown -------------------- */}
        <div className="regbars-toolbar">
          <h2>Daily registrations</h2>
          <div className="regbars-period" role="group" aria-label="Period">
            {PERIODS.map((days) => (
              <button
                key={days}
                type="button"
                className={period === days ? 'active' : ''}
                aria-pressed={period === days}
                onClick={() => setPeriod(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
        <div className="regbars-row">
          <RegistrationBars
            title="New customers"
            series={registrations.data?.customers}
            days={registrations.data?.days || period}
            color={CUSTOMER_COLOR}
            isDarkMode={isDarkMode}
            loading={registrations.loading}
            error={registrations.error}
            onRetry={() => fetchRegistrations(period)}
          />
          <RegistrationBars
            title="New vendors"
            series={registrations.data?.vendors}
            days={registrations.data?.days || period}
            color={VENDOR_COLOR}
            isDarkMode={isDarkMode}
            loading={registrations.loading}
            error={registrations.error}
            onRetry={() => fetchRegistrations(period)}
          />
        </div>

        <div className="dashboard-split">
          <div className="panel">
            <div className="panel-head">
              <div>
                <h2>Listings by type</h2>
                <p>Active, pending and inactive across the catalogue</p>
              </div>
            </div>

            {listingRows.length === 0 ? (
              <p className="panel-empty">You do not have access to any listing type.</p>
            ) : (
              <ul className="breakdown">
                {listingRows.map((row) => {
                  const d = listings[row.key];
                  const total = d.total || 0;
                  const pct = (n) => (total === 0 ? 0 : (n / total) * 100);
                  return (
                    <li key={row.key}>
                      <div className="breakdown-head">
                        <Link to={row.to}>{row.label}</Link>
                        <span>
                          <b>{compact.format(d.active)}</b> / {compact.format(total)}
                        </span>
                      </div>
                      <div className="breakdown-bar" title={`${d.active} active, ${d.pending} pending, ${d.inactive} inactive`}>
                        <span className="seg seg-active" style={{ width: `${pct(d.active)}%` }} />
                        <span className="seg seg-pending" style={{ width: `${pct(d.pending)}%` }} />
                        <span className="seg seg-inactive" style={{ width: `${pct(d.inactive)}%` }} />
                      </div>
                      <div className="breakdown-legend">
                        <span><i className="dot dot-active" />{compact.format(d.active)} active</span>
                        <span><i className="dot dot-pending" />{compact.format(d.pending)} pending</span>
                        <span><i className="dot dot-inactive" />{compact.format(d.inactive)} inactive</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ---------------------------- secondary ---------------------------- */}
        <div className="mini-grid">
          <div className="mini">
            <span className="mini-label">Verified customers</span>
            <strong>{compact.format(customers.verified)}</strong>
            <span className="mini-sub">{compact.format(customers.active)} active accounts</span>
          </div>
          <div className="mini">
            <span className="mini-label">New this week</span>
            <strong>{compact.format(customers.new7d)}</strong>
            <span className="mini-sub">customers in the last 7 days</span>
          </div>
          <div className="mini">
            <span className="mini-label">Owner leads converted</span>
            <strong>{compact.format(content.completedOwnerLeads)}</strong>
            <span className="mini-sub">{compact.format(attention.pendingOwnerLeads)} still pending</span>
          </div>
          <div className="mini">
            <span className="mini-label">Destinations &amp; pushes</span>
            <strong>{compact.format(content.destinations)}</strong>
            <span className="mini-sub">{compact.format(content.notifications)} notifications created</span>
          </div>
        </div>

        {/* -------------------------- quick actions -------------------------- */}
        {quickActions.length > 0 && (
          <div className="panel">
            <div className="panel-head">
              <div>
                <h2>Quick actions</h2>
                <p>Only what your role lets you create</p>
              </div>
            </div>
            <div className="actions">
              {quickActions.map((a) => (
                <Link key={a.to} to={a.to} className="action">
                  <span className="action-icon" aria-hidden="true">{a.icon}</span>
                  <span>
                    <b>{a.label}</b>
                    <small>{a.desc}</small>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="dashboard-stamp">
          Updated {new Date(stats.generatedAt).toLocaleString('en-IN')}
        </p>
      </div>
    </Layout>
  );
};

export default Dashboard;
